import React, { useState, useEffect } from 'react';
import { PaymentManager } from '../PaymentManager.js';
import { SubscriptionManager } from '../SubscriptionManager.js';
import { AnalyticsManager } from '../AnalyticsManager.js';
import { LoggingManager } from '../LoggingManager.js';

const PaymentFlowTester = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState('');

  const addTestResult = (testName, status, details = '') => {
    const result = {
      id: Date.now() + Math.random(),
      testName,
      status, // 'pass', 'fail', 'warning'
      details,
      timestamp: new Date().toISOString()
    };
    setTestResults(prev => [...prev, result]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  // Test 1: Payment Manager Initialization
  const testPaymentManagerInit = async () => {
    setCurrentTest('Payment Manager Initialization');
    try {
      PaymentManager.initialize();
      
      // Check if Stripe is available
      const stripeLoaded = await PaymentManager.loadStripe();
      
      if (stripeLoaded) {
        addTestResult('Payment Manager Init', 'pass', 'Stripe loaded successfully');
      } else {
        addTestResult('Payment Manager Init', 'warning', 'Stripe not loaded (expected in development)');
      }
    } catch (error) {
      addTestResult('Payment Manager Init', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 2: Subscription Manager Functionality
  const testSubscriptionManager = async () => {
    setCurrentTest('Subscription Manager');
    try {
      SubscriptionManager.initialize();
      
      // Test tier checking
      const currentTier = SubscriptionManager.getCurrentTier();
      const isPro = SubscriptionManager.hasFeature(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR);
      
      addTestResult('Subscription Manager Init', 'pass', `Current tier: ${currentTier}, Pro features: ${isPro}`);
      
      // Test pricing
      const pricing = SubscriptionManager.getPricing();
      if (pricing && pricing.PRO_MONTHLY && pricing.PRO_YEARLY) {
        addTestResult('Pricing Configuration', 'pass', `Monthly: $${pricing.PRO_MONTHLY}, Yearly: $${pricing.PRO_YEARLY}`);
      } else {
        addTestResult('Pricing Configuration', 'fail', 'Pricing not properly configured');
      }
    } catch (error) {
      addTestResult('Subscription Manager', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 3: Payment Method Creation (Mock)
  const testPaymentMethodCreation = async () => {
    setCurrentTest('Payment Method Creation');
    try {
      const mockPaymentData = {
        cardNumber: '4242424242424242', // Stripe test card
        expMonth: '12',
        expYear: '2025',
        cvc: '123',
        name: 'Test User',
        email: 'test@example.com'
      };

      // This would normally call PaymentManager.createPaymentMethod
      // For testing, we'll simulate the response
      const mockResult = {
        success: true,
        paymentMethodId: 'pm_test_1234567890',
        error: null
      };

      if (mockResult.success) {
        addTestResult('Payment Method Creation', 'pass', `Mock payment method created: ${mockResult.paymentMethodId}`);
      } else {
        addTestResult('Payment Method Creation', 'fail', `Error: ${mockResult.error}`);
      }
    } catch (error) {
      addTestResult('Payment Method Creation', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 4: Subscription Creation (Mock)
  const testSubscriptionCreation = async () => {
    setCurrentTest('Subscription Creation');
    try {
      const mockSubscriptionData = {
        planId: 'pro_monthly',
        customerEmail: 'test@example.com',
        customerName: 'Test User',
        paymentMethodId: 'pm_test_1234567890'
      };

      // Mock subscription creation
      const mockResult = {
        success: true,
        subscriptionId: 'sub_test_1234567890',
        customerId: 'cus_test_1234567890',
        error: null
      };

      if (mockResult.success) {
        addTestResult('Subscription Creation', 'pass', `Mock subscription created: ${mockResult.subscriptionId}`);
      } else {
        addTestResult('Subscription Creation', 'fail', `Error: ${mockResult.error}`);
      }
    } catch (error) {
      addTestResult('Subscription Creation', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 5: Webhook Handling (Mock)
  const testWebhookHandling = async () => {
    setCurrentTest('Webhook Handling');
    try {
      // Mock webhook events
      const webhookEvents = [
        { type: 'customer.subscription.created', status: 'active' },
        { type: 'customer.subscription.updated', status: 'active' },
        { type: 'invoice.payment_succeeded', status: 'paid' },
        { type: 'invoice.payment_failed', status: 'failed' }
      ];

      let successCount = 0;
      for (const event of webhookEvents) {
        // Simulate webhook processing
        const processed = await simulateWebhookProcessing(event);
        if (processed) successCount++;
      }

      if (successCount === webhookEvents.length) {
        addTestResult('Webhook Handling', 'pass', `All ${webhookEvents.length} webhook events processed successfully`);
      } else {
        addTestResult('Webhook Handling', 'fail', `Only ${successCount}/${webhookEvents.length} webhook events processed`);
      }
    } catch (error) {
      addTestResult('Webhook Handling', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 6: Analytics and Logging Integration
  const testAnalyticsIntegration = async () => {
    setCurrentTest('Analytics Integration');
    try {
      // Test analytics tracking
      if (window.AnalyticsManager) {
        AnalyticsManager.trackEvent('payment_test_started', { testType: 'comprehensive' });
        addTestResult('Analytics Tracking', 'pass', 'Analytics event tracked successfully');
      } else {
        addTestResult('Analytics Tracking', 'warning', 'AnalyticsManager not available');
      }

      // Test logging
      if (window.LoggingManager) {
        LoggingManager.info('Payment flow test completed', { testResults: testResults.length });
        addTestResult('Logging Integration', 'pass', 'Logging event recorded successfully');
      } else {
        addTestResult('Logging Integration', 'warning', 'LoggingManager not available');
      }
    } catch (error) {
      addTestResult('Analytics Integration', 'fail', `Error: ${error.message}`);
    }
  };

  // Test 7: Error Handling
  const testErrorHandling = async () => {
    setCurrentTest('Error Handling');
    try {
      // Test invalid payment data
      const invalidPaymentData = {
        cardNumber: '1234567890123456', // Invalid card
        expMonth: '13', // Invalid month
        expYear: '2020', // Expired year
        cvc: '12', // Invalid CVC
        name: '',
        email: 'invalid-email'
      };

      // Simulate validation
      const validationErrors = validatePaymentData(invalidPaymentData);
      
      if (validationErrors.length > 0) {
        addTestResult('Error Handling', 'pass', `Validation caught ${validationErrors.length} errors: ${validationErrors.join(', ')}`);
      } else {
        addTestResult('Error Handling', 'fail', 'Validation should have caught invalid data');
      }
    } catch (error) {
      addTestResult('Error Handling', 'fail', `Error: ${error.message}`);
    }
  };

  // Helper function to simulate webhook processing
  const simulateWebhookProcessing = async (event) => {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Simulate successful processing
    return true;
  };

  // Helper function to validate payment data
  const validatePaymentData = (data) => {
    const errors = [];
    
    if (!data.cardNumber || data.cardNumber.length < 13) {
      errors.push('Invalid card number');
    }
    
    if (!data.expMonth || data.expMonth < 1 || data.expMonth > 12) {
      errors.push('Invalid expiry month');
    }
    
    if (!data.expYear || data.expYear < new Date().getFullYear()) {
      errors.push('Invalid expiry year');
    }
    
    if (!data.cvc || data.cvc.length < 3) {
      errors.push('Invalid CVC');
    }
    
    if (!data.name || data.name.trim().length < 2) {
      errors.push('Invalid name');
    }
    
    if (!data.email || !data.email.includes('@')) {
      errors.push('Invalid email');
    }
    
    return errors;
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunning(true);
    clearResults();
    
    const tests = [
      testPaymentManagerInit,
      testSubscriptionManager,
      testPaymentMethodCreation,
      testSubscriptionCreation,
      testWebhookHandling,
      testAnalyticsIntegration,
      testErrorHandling
    ];

    for (const test of tests) {
      try {
        await test();
        // Small delay between tests
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        addTestResult('Test Execution', 'fail', `Test failed: ${error.message}`);
      }
    }

    setIsRunning(false);
    setCurrentTest('');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-600 bg-green-100';
      case 'fail': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return '✓';
      case 'fail': return '✗';
      case 'warning': return '⚠';
      default: return '?';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Payment Flow Testing Suite</h1>
        
        {/* Test Controls */}
        <div className="mb-6">
          <div className="flex gap-4 mb-4">
            <button
              onClick={runAllTests}
              disabled={isRunning}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isRunning ? 'Running Tests...' : 'Run All Tests'}
            </button>
            
            <button
              onClick={clearResults}
              disabled={isRunning}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Clear Results
            </button>
          </div>
          
          {isRunning && (
            <div className="text-blue-600 font-medium">
              Currently running: {currentTest}
            </div>
          )}
        </div>

        {/* Test Results */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Results</h2>
          
          {testResults.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No tests run yet. Click "Run All Tests" to start testing the payment flow.
            </div>
          ) : (
            <div className="space-y-3">
              {testResults.map((result) => (
                <div key={result.id} className="flex items-start p-4 border rounded-lg">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4 ${getStatusColor(result.status)}`}>
                    {getStatusIcon(result.status)}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{result.testName}</div>
                    <div className="text-sm text-gray-600 mt-1">{result.details}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(result.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {testResults.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Test Summary</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-green-600 font-medium">
                  Passed: {testResults.filter(r => r.status === 'pass').length}
                </span>
              </div>
              <div>
                <span className="text-red-600 font-medium">
                  Failed: {testResults.filter(r => r.status === 'fail').length}
                </span>
              </div>
              <div>
                <span className="text-yellow-600 font-medium">
                  Warnings: {testResults.filter(r => r.status === 'warning').length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Test Information */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">About These Tests</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>• <strong>Payment Manager Init:</strong> Tests Stripe integration and initialization</p>
            <p>• <strong>Subscription Manager:</strong> Verifies subscription tier management and pricing</p>
            <p>• <strong>Payment Method Creation:</strong> Tests payment method creation (mocked)</p>
            <p>• <strong>Subscription Creation:</strong> Tests subscription creation flow (mocked)</p>
            <p>• <strong>Webhook Handling:</strong> Tests webhook event processing (mocked)</p>
            <p>• <strong>Analytics Integration:</strong> Verifies event tracking and logging</p>
            <p>• <strong>Error Handling:</strong> Tests validation and error scenarios</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFlowTester;
